import { faCalendarDay, faClock } from "@fortawesome/duotone-light-svg-icons"
import {
  faBan,
  faChevronRight,
  faExclamationTriangle,
} from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  JoonUIColor,
  Typography,
  SessionBillingPeriod,
  SessionBooking,
  Button,
  QuotesIcon,
} from "@joonapp/web-shared"
import dayjs from "dayjs"
import { useRef, useState, useEffect } from "react"

import useSession from "./hooks/useSession"
import Tooltip from "../../../components/tooltip/Tooltip"
import useUserRole from "../../../hooks/useUserRole"
import { formatDate } from "../../../util/dates"

const SessionSlot = ({
  session,
  billingPeriod,
}: {
  session: SessionBooking | undefined
  billingPeriod?: SessionBillingPeriod
}) => {
  const isBooked = !!session
  const containerRef = useRef<HTMLDivElement>(null)
  const [isSmallElement, setIsSmallElement] = useState(false)

  // On resize, check if element is small
  useEffect(() => {
    const element = containerRef.current
    if (!element) return

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setIsSmallElement(entry.contentRect.width < 450)
      }
    })
    resizeObserver.observe(element)
    return () => resizeObserver.disconnect()
  }, [])

  const { handleViewNotes, sessionIsUpcoming, sessionIsPast } =
    useSession(session)

  const sessionDuration = dayjs(session?.end_datetime).diff(
    dayjs(session?.start_datetime),
    "minutes"
  )

  if (!isBooked) return <OpenSessionSlot billingPeriod={billingPeriod} />

  return (
    <div
      className={`flex flex-col items-center justify-between rounded-md w-full border overflow-hidden ${
        sessionIsUpcoming ? "border-primary" : "bg-white border-borderPrimary"
      }`}
      ref={containerRef}
    >
      <button onClick={handleViewNotes} className="w-full">
        <div
          className={`flex flex-row items-center justify-between w-full px-4 py-3 text-left  ${
            isSmallElement ? "flex-col gap-1 items-start" : "gap-2 items-center"
          }
          ${
            sessionIsUpcoming
              ? "border-b border-borderPrimary bg-yellow-100 hover:bg-yellow-200"
              : "bg-white hover:bg-gray-50"
          }
          `}
        >
          <div className="flex flex-col gap-1">
            <Typography variant="bodyBold" textAlign="left">
              {formatDate(new Date(session?.start_datetime), "ddd, MMM D")}
              {", "}
              {formatDate(new Date(session?.start_datetime), "h:mm A", false)}
            </Typography>
            <div
              className={`flex flex-row items-center gap-1 p-1 py-[2px] rounded-md w-fit ${
                sessionIsPast ? "bg-gray-100" : "bg-indigo-100"
              }`}
            >
              <FontAwesomeIcon
                icon={faClock}
                color={
                  sessionIsPast
                    ? JoonUIColor.text.secondary
                    : JoonUIColor.icon.accent
                }
              />
              <Typography
                variant="bodySmall"
                color={
                  sessionIsPast
                    ? JoonUIColor.text.secondary
                    : JoonUIColor.text.primaryAccent
                }
              >
                {sessionDuration} min
              </Typography>
            </div>
          </div>
          {(session?.marked_no_show || session?.marked_late_cancellation) && (
            <div className="flex flex-row items-center justify-center gap-1 px-2 py-1 mr-auto ml-2 bg-red-100 rounded-full">
              <FontAwesomeIcon
                icon={faBan}
                color={JoonUIColor.semantic.destructive}
              />
              <Typography variant="bodySmall" style={{ whiteSpace: "nowrap" }}>
                {session?.marked_no_show ? "No-show" : "Late cancel"}
              </Typography>
            </div>
          )}
          <FontAwesomeIcon
            icon={faChevronRight}
            color={JoonUIColor.icon.neutral}
            fontSize={16}
            data-tooltip-id="tooltip"
          />
          <Tooltip id="tooltip">
            <Typography variant="bodyXSmall" color={JoonUIColor.text.inverted}>
              View session notes
            </Typography>
          </Tooltip>
        </div>
      </button>

      {session?.topic_to_discuss_notes.note && (
        <div
          className={`bg-white w-full px-4 py-3 flex flex-row gap-2 justify-start ${
            sessionIsUpcoming ? "" : "pt-0"
          }`}
        >
          <div className="flex w-full gap-2 p-2 rounded-md bg-bgXLightGray">
            <div className="min-w-5">
              <QuotesIcon />
            </div>
            <Typography variant="bodySmall" color={JoonUIColor.text.secondary}>
              {session?.topic_to_discuss_notes.note}
            </Typography>
          </div>
        </div>
      )}
    </div>
  )
}

export default SessionSlot

const OpenSessionSlot = ({
  billingPeriod,
}: {
  billingPeriod: SessionBillingPeriod | undefined
}) => {
  const { handleSchedule } = useSession(undefined)
  const { isClientOfOtherCoach } = useUserRole()

  const onClick = () => {
    if (!billingPeriod) return
    handleSchedule(billingPeriod)
  }

  return (
    <div
      className={`flex flex-row items-center justify-between px-4 py-3 rounded-md w-full bg-white border border-borderPrimary`}
    >
      <div className="flex flex-row items-center justify-center gap-2">
        <FontAwesomeIcon
          icon={faExclamationTriangle}
          color={JoonUIColor.semantic.warning}
          fontSize={16}
        />
        <Typography variant="bodySmall" color={JoonUIColor.semantic.warning}>
          Not scheduled
        </Typography>
      </div>
      <Button
        text="Schedule"
        onClick={onClick}
        size="small"
        icon={faCalendarDay}
        disabled={isClientOfOtherCoach}
      />
    </div>
  )
}
